import React, { FC, useState } from 'react';

import { QUERY_KEYS } from 'api/queries';
import View from 'components/core/View/View.view';
import AlertModal from 'components/modals/AlertModal';
import Loading from 'components/primitives/Loading';
import Pagination from 'components/primitives/Pagination';
import BadgeView from 'components/primitives/StatusBadge/Badge.view';
import Table from 'components/primitives/Table';
import ToggleSwitch from 'components/primitives/ToggleSwitch';
import NoItemsFound from 'components/vectors/NoItemsFound';
import { routesSettings } from 'constants/routes';
import { format } from 'date-fns';
import ModalReference from 'helperClasses/modalReference';
import { useNavigate } from 'react-router-dom';
import { $itemRentDetails } from 'state/stores/rent-details';
import { getNetworkImage } from 'utils/assets';
import { LendTableViewProps } from './LendTable.props';

const LendTableView: FC<LendTableViewProps> = ({
  products,
  isLoading,
  filteredItems,
  totalPages,
  setPage,
  currentPage,
  onToggleItem,
  isToggling,
}) => {
  const navigate = useNavigate();
  const confirmationModalRef = new ModalReference();
  const [pendingToggleItemId, setPendingToggleItemId] = useState<string | null>(
    null,
  );

  const showProductDetails = (index: number) => {
    const productId = filteredItems[index];
    QUERY_KEYS.invalidate(QUERY_KEYS.FETCH_RENT_DETAILS_KEY);
    QUERY_KEYS.invalidate(QUERY_KEYS.FETCH_ONGIONG_RENTS_KEY);
    $itemRentDetails.actions.reset();
    navigate(routesSettings.MAIN_LEND_PRODUCT.getPath(productId._id!), {
      replace: false,
    });
  };

  const handleToggleChange = (
    itemId: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    event.stopPropagation(); // Prevent row click when toggling

    // Find the product to check if it's currently enabled
    const product = filteredItems.find((item) => item._id === itemId);
    const isCurrentlyEnabled = product?.status !== 'disabled';
    setPendingToggleItemId(itemId);
    // Only show confirmation for disabling items
    if (isCurrentlyEnabled) {
      confirmationModalRef.open();
    } else {
      // Directly enable the item without confirmation
      onToggleItem(itemId);
    }
  };

  const handleConfirmToggle = () => {
    if (pendingToggleItemId) {
      onToggleItem(pendingToggleItemId);
      confirmationModalRef.close();
    }
  };

  const handleCancelToggle = () => {
    setPendingToggleItemId(null);
    confirmationModalRef.close();
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <View.Container>
      <div>
        <div className="space-y-8">
          <Table
            itemNotFoundComponent={
              <NoItemsFound description='We couldn&apos;t find anything matching your search criteria. Try adjusting your filters or search terms. Click "Create Item" above to start adding today.' />
            }
            headers={[
              'Image',
              'Name',
              'Category',
              'Status',
              'Date Created',
              'Publicly Listed',
            ]}
            itemCount={filteredItems.length ?? 0}
            onClickItem={(index) => showProductDetails(index)}
            builder={function (index: number): React.ReactNode {
              const product = filteredItems[index];
              // Determine if item is publicly listed based on status
              // Assuming status "available" means publicly listed, "disabled" means not listed
              const isPubliclyListed = product.status !== 'disabled';

              return (
                <>
                  <td className="whitespace-nowrap  px-6 py-4 font-medium w-4">
                    <img
                      className="w-10 h-10 rounded-md object-cover"
                      src={getNetworkImage(
                        product.featuredPhoto ?? product?.photos[0],
                      )}
                      alt={product.title}
                    />
                  </td>
                  <td className="whitespace-nowrap text-sm text-black-700 font-medium font-primary capitalize  px-6 py-4">
                    {product.title}
                  </td>
                  <td className="whitespace-nowrap text-sm text-gray-700 font-medium font-primary capitalize px-6 py-4 ">
                    {product?.categories[0].name}
                  </td>
                  <td className="whitespace-nowrap py-4 w-48">
                    <BadgeView
                      value={product.status}
                      textColor="white"
                      bgColor="#22CC7A"
                      className="rounded-full w-24"
                    />
                  </td>
                  <td className="whitespace-nowrap text-gray-700 font-medium font-primary capitalize px-6 py-4 w-64">
                    {format(
                      new Date(product.createdAt),
                      'MMMM dd, yyyy hh:mm:ss a',
                    )}
                  </td>
                  <td
                    onClick={(e) => e.stopPropagation()}
                    className="whitespace-nowrap text-gray-700 font-medium font-primary capitalize px-6 py-4">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch
                        checked={isPubliclyListed}
                        onChange={(event) => {
                          handleToggleChange(product._id, event);
                        }}
                        disabled={isToggling}
                        className="flex justify-center"
                      />
                      {isToggling && pendingToggleItemId === product._id && (
                        <Loading.LoaderSecondary />
                      )}
                    </div>
                  </td>
                </>
              );
            }}
          />
          <div className="flex justify-end">
            {!totalPages ? (
              <></>
            ) : (
              <Pagination
                totalPages={totalPages!}
                currentPageNo={currentPage!}
                onPageChange={function (page: number): void {
                  setPage && setPage(page);
                }}
              />
            )}
          </div>
        </div>

        <AlertModal
          ref={confirmationModalRef.getReference()}
          title="Disable Item Listing?"
          description="Are you sure you want to disable this item's public listing? It will no longer be visible to the public in item lists, search results, or any featured sections. You can re-enable it anytime from your dashboard."
          buttonTitle="Disable Item"
          buttonIsLoading={isToggling}
          onClickButton={handleConfirmToggle}
          onClose={handleCancelToggle}
          showCancelButton={true}
        />
      </div>
    </View.Container>
  );
};

export default LendTableView;
